import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  classifyStudent,
  daysSinceLastSession,
  RetentionCategory,
} from '@/lib/retention';
import { Student } from '@/lib/types';

export interface StudentWithRetention extends Student {
  category: RetentionCategory;
  daysSinceLast: number | null;
  sessionsLastWeek: number;
}

export interface RetentionKPIs {
  activeStudents: number;
  engagementRate: number; // %
  growthRate: number;     // % vs mês anterior (criação de alunos)
  recoveryRate: number;   // % (recuperados / total)
}

export interface RetentionData {
  students: StudentWithRetention[];
  byCategory: Record<RetentionCategory, StudentWithRetention[]>;
  counts: Record<RetentionCategory, number>;
  total: number;
  kpis: RetentionKPIs;
}

const EMPTY: RetentionData = {
  students: [],
  byCategory: { engajado: [], oscilando: [], em_risco: [], abandono: [], recuperado: [] },
  counts: { engajado: 0, oscilando: 0, em_risco: 0, abandono: 0, recuperado: 0 },
  total: 0,
  kpis: { activeStudents: 0, engagementRate: 0, growthRate: 0, recoveryRate: 0 },
};

export function useRetention(personalId: string) {
  return useQuery({
    queryKey: ['retention', personalId],
    queryFn: async (): Promise<RetentionData> => {
      // 1) Alunos não-self do personal, ativos
      const { data: students, error: sErr } = await supabase
        .from('students')
        .select('*')
        .eq('personal_id', personalId)
        .eq('is_self', false)
        .eq('status', 'active');
      if (sErr) throw sErr;
      if (!students || students.length === 0) return EMPTY;

      const studentIds = students.map((s: Student) => s.id);

      // 2) Sessions dos últimos 90 dias (suficiente pra classificar)
      const ninetyDaysAgo = new Date(Date.now() - 90 * 86_400_000).toISOString();
      const { data: sessions, error: ssErr } = await supabase
        .from('workout_sessions')
        .select('student_id, completed_at')
        .in('student_id', studentIds)
        .gte('completed_at', ninetyDaysAgo)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false });
      if (ssErr) throw ssErr;

      // 3) Agrupa sessions por aluno
      const sessionsByStudent: Record<string, { completed_at: string }[]> = {};
      for (const s of (sessions ?? []) as { student_id: string; completed_at: string }[]) {
        (sessionsByStudent[s.student_id] ||= []).push({ completed_at: s.completed_at });
      }

      // 4) Classifica
      const now = Date.now();
      const sevenDaysAgo = now - 7 * 86_400_000;

      const enriched: StudentWithRetention[] = (students as Student[]).map(s => {
        const studentSessions = sessionsByStudent[s.id] ?? [];
        const category = classifyStudent(studentSessions);
        const daysLast = daysSinceLastSession(studentSessions);
        const sessionsLastWeek = studentSessions.filter(
          x => new Date(x.completed_at).getTime() >= sevenDaysAgo
        ).length;
        return { ...s, category, daysSinceLast: daysLast, sessionsLastWeek };
      });

      // 5) Agrupa por categoria
      const byCategory: Record<RetentionCategory, StudentWithRetention[]> = {
        engajado: [], oscilando: [], em_risco: [], abandono: [], recuperado: [],
      };
      for (const s of enriched) byCategory[s.category].push(s);

      const counts = {
        engajado: byCategory.engajado.length,
        oscilando: byCategory.oscilando.length,
        em_risco: byCategory.em_risco.length,
        abandono: byCategory.abandono.length,
        recuperado: byCategory.recuperado.length,
      };
      const total = enriched.length;

      // 6) KPIs
      const engagementRate = total > 0
        ? ((counts.engajado + counts.recuperado) / total) * 100
        : 0;

      // Crescimento: novos alunos no último mês vs mês anterior
      const thirtyDaysAgo = now - 30 * 86_400_000;
      const sixtyDaysAgo = now - 60 * 86_400_000;
      const newThisMonth = enriched.filter(s =>
        new Date(s.created_at).getTime() >= thirtyDaysAgo
      ).length;
      const newLastMonth = enriched.filter(s => {
        const t = new Date(s.created_at).getTime();
        return t >= sixtyDaysAgo && t < thirtyDaysAgo;
      }).length;
      const growthRate = newLastMonth > 0
        ? ((newThisMonth - newLastMonth) / newLastMonth) * 100
        : (newThisMonth > 0 ? 100 : 0);

      const recoveryRate = total > 0 ? (counts.recuperado / total) * 100 : 0;

      const round1 = (n: number) => Math.round(n * 10) / 10;

      return {
        students: enriched,
        byCategory,
        counts,
        total,
        kpis: {
          activeStudents: total,
          engagementRate: round1(engagementRate),
          growthRate: round1(growthRate),
          recoveryRate: round1(recoveryRate),
        },
      };
    },
    enabled: !!personalId,
    staleTime: 60 * 1000,
  });
}
